<script lang="ts" setup>
import type { LayoutAndNavQuery } from '@autobid/strapi-integration/graphql/graphql'
import { openOneTrustModal } from '@autobid/ui/utils/openOneTrustModal'

interface Props {
  footer: NonNullable<NonNullable<LayoutAndNavQuery['layouts'][number]>['footer']>
}

defineProps<Props>()

const OT_SETTINGS_LINK = '#uc-central-modal-show'

interface LinkAttributes {
  href?: string
  title?: string
}
function getLinkAttributes({ href, title }: LinkAttributes) {
  if (!href?.length)
    return {}

  return {
    href,
    title,
    rel: 'nofollow noopener',
  }
}

function handleClickLink(url: string) {
  if (url === OT_SETTINGS_LINK) {
    openOneTrustModal()
  }
}
</script>

<template>
  <div
    class="footer-wrapper bg-[var(--footer-background-color)] text-[var(--footer-text-color)]"
  >
    <footer
      class="footer container mx-auto border-t border-[var(--footer-border-color)] px-3 py-10"
    >
      <div class="flex w-full flex-col gap-6">
        <div
          v-if="footer.logos?.length || footer.columns?.length"
          class="flex w-full flex-wrap gap-6"
        >
          <!-- logos -->
          <div
            v-if="footer.logos?.length"
            class="mt-9 flex w-full flex-wrap items-start gap-3 border-t py-2 pt-3 md:w-80 lg:w-96"
          >
            <template v-for="logo in footer.logos">
              <common-link
                v-if="logo"
                :key="logo.id"
                v-bind="getLinkAttributes({ href: String(logo.url), title: logo.title })"
              >
                <CommonStrapiImage
                  :data="logo.image"
                  class="h-20 w-auto"
                  :alt="logo.title"
                  loading="lazy"
                />
              </common-link>
            </template>
          </div>

          <!-- links -->
          <div v-if="footer.columns?.length" class="flex-1">
            <div class="columns-title pb-2 text-lg">
              {{ footer.columnsTitle }}
            </div>
            <div
              class="columns-wrapper flex flex-col flex-wrap gap-6 border-t py-2 sm:flex-row"
            >
              <template v-for="column in footer.columns">
                <div
                  v-if="column"
                  :key="column.id"
                  class="flex flex-col"
                >
                  <span
                    class="footer-title mb-2 font-bold uppercase tracking-wider"
                  >
                    {{ column.title }}
                  </span>

                  <template v-for="link in column.links">
                    <common-link
                      v-if="link"
                      :key="link.id"
                      :="{
                        href: link.url === OT_SETTINGS_LINK ? '#' : link.url,
                        id: link.url === OT_SETTINGS_LINK ? 'ot-sdk' : link.id
                      }"
                      class="mb-1 pb-1 text-sm hover:text-[var(--footer-text-hover-color)]"
                      :class="
                        link.url === OT_SETTINGS_LINK ? 'ot-sdk-show-settings' : ''
                      "
                    >
                      {{ link.text }}
                    </common-link>
                  </template>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- images -->
        <div v-if="footer.imagesRows?.length" class="border-t">
          <template v-for="row in footer.imagesRows">
            <div
              v-if="row"
              :key="row.id"
              class="flex w-full flex-wrap justify-center gap-3 gap-y-5 pt-4 md:justify-start md:gap-x-8 lg:gap-x-11"
            >
              <template v-for="image in row.logos">
                <common-link
                  v-if="image"
                  :key="image.image.documentId"
                  v-bind="getLinkAttributes({ href: String(image.url), title: image.title })"
                >
                  <CommonStrapiImage
                    :data="image.image"
                    class="h-20 w-auto opacity-50 grayscale transition-opacity duration-300 hover:opacity-100 hover:grayscale-0"
                    :alt="image.title"
                    loading="lazy"
                  />
                </common-link>
              </template>
            </div>
          </template>
        </div>

        <div
          v-if="footer.copyright && footer.links?.length"
          class="flex w-full flex-wrap-reverse gap-6 border-t py-3 text-xs lg:flex-nowrap"
        >
          <!-- copyright -->
          <div class="copyright">
            {{ footer.copyright }}
          </div>

          <!-- links -->
          <div
            class="flex flex-1 flex-wrap justify-center gap-4 gap-y-2 md:justify-end"
          >
            <template v-for="link in footer.links">
              <div v-if="link" :key="link.id">
                <common-link
                  class="hover:text-[var(--footer-text-hover-color)]"
                  :href="link.url === OT_SETTINGS_LINK ? '#' : link.url"
                  @click="handleClickLink(link.url)"
                >
                  {{ link.text }}
                </common-link>
              </div>
            </template>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>
